import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/cn';
import { type } from '@/theme/typography';

interface Props {
  /** One-based, as the API counts pages — the address's own value, never one derived from a payload. */
  page: number;
  /** The rows on this page. `0` is a page past the end, which is a state and not an error. */
  shown: number;
  /** The server's `count` for the whole list, which is what makes the range knowable. */
  count: number;
  /** The page size the range does its arithmetic with — the constant the query was asked with. */
  pageSize: number;
  onGoToPage: (page: number) => void;
}

/**
 * §6.9's pager, for both tabs — **§6.8's shape, reused rather than re-derived** (TASK-146).
 *
 * ⚠️ **The rules it inherits, each of which §6.8 or §6.3 paid for once:**
 *
 *  * **The range is stated rather than implied.** *"Showing 1–100 of 137"* — `count` beside the page is
 *    what makes a truncated read knowable rather than guessable, which is the property §6.9's old
 *    truncation notice existed for and the only thing that notice could do.
 *  * **Both ends are disabled**, and the last one is decided from `count` rather than from a short
 *    page: a page that happens to come back short is not the end of the list, and a *Next* that walked
 *    on for ever is what an empty page would produce.
 *  * **The page is not clamped from the response** (`readQuestionFilters`): `count` is only known once
 *    the answer arrives, so clamping would make the screen rewrite the address from a payload — a
 *    second writer of the page and a history entry nobody navigated to.
 *  * **A page is a new address, not a correction of the previous one**, so Back walks back through
 *    pages. The caller does that (`questionSearch`); this component only says which page to go to.
 *
 * It is **one component for two tabs** on purpose: two pagers in one screen that disagreed about
 * whether *Next* is disabled at the end would be the same control behaving differently depending on
 * which tab you pressed it from.
 */
export function ListPager({ page, shown, count, pageSize, onGoToPage }: Props) {
  const { t } = useTranslation('questions');

  const first = shown === 0 ? 0 : (page - 1) * pageSize + 1;
  const last = shown === 0 ? 0 : first + shown - 1;
  const isLastPage = page * pageSize >= count;

  return (
    <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
      <p className={cn(type.metadata, type.numeral)}>{t('pager.range', { first, last, count })}</p>
      <div className="flex items-center gap-2">
        <Button
          variant="secondary"
          size="sm"
          disabled={page <= 1}
          onClick={() => onGoToPage(page - 1)}
        >
          {t('pager.previous')}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          disabled={isLastPage}
          onClick={() => onGoToPage(page + 1)}
        >
          {t('pager.next')}
        </Button>
      </div>
    </div>
  );
}
